import { View } from "react-native";
import React, { useMemo, useState } from "react";
import Dialog, { DialogProps } from "./Dialog";
import { IndexPath, Select, SelectItem, Text } from "@ui-kitten/components";
import { AccountTicketDetails } from "features/types";
import useResolveNativeUrl from "hooks/useResolveNativeUrl";
import useCurrentUser from "hooks/useCurrentUser";
import { useAccount } from "hooks/useAccount";
import { useMutation } from "@tanstack/react-query";
import { initiateTransferFetcher } from "queries/ticket.queries";
import i18n from "plugins/localization";
import useShare from "hooks/useShare";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { getUserName } from "utils/userUtils";
import { formatEventDate } from "utils/eventUtils";

interface ShareDialogProps extends DialogProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  label: string;
  accountEvent: AccountTicketDetails;
  path: string;
  type: "guest" | "seats";
}

const ShareDialog: React.FC<ShareDialogProps> = (props) => {
  const { visible, setVisible, title, label, accountEvent, path, type } = props;
  const { account } = useAccount();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const maxQuantity = (() => {
    let quantity = 0;
    if (type === "guest") {
      quantity = accountEvent.tickets.filter(
        (ticket) =>
          ticket.reservedByAccountId === account!.id &&
          ticket.planPackageComponentType.includes("GUEST")
      ).length;
    } else {
      quantity = accountEvent.tickets.filter(
        (ticket) =>
          ticket.reservedByAccountId === account!.id &&
          !ticket.planPackageComponentType.includes("GUEST") &&
          ticket.transferability !== "NONE"
      ).length;
    }
    return quantity;
  })();

  const [selectedIndex, setSelectedIndex] = useState(new IndexPath(0));
  const [transferCode, setTransferCode] = useState("");
  const { currentUser } = useCurrentUser();
  const nativeUrl = useResolveNativeUrl({
    path,
    accountEvent,
    transferCode: transferCode!,
  });
  const { handleShare, copied, shareError } = useShare();
  const { mutateAsync: initiateTransferRequest, isLoading } = useMutation(
    initiateTransferFetcher
  );

  const message = useMemo(() => {
    if (!currentUser) return "";
    const {
      event: { tenantAlias, opponentAlias },
    } = accountEvent!;
    return i18n.t("components.ShareDialog.message", {
      userName: getUserName(currentUser!),
      opponent: opponentAlias,
      tenant: tenantAlias,
      eventDate: formatEventDate(accountEvent!.event),
      link: nativeUrl,
    });
  }, [currentUser, nativeUrl]);

  const findGuestComponentType = () => {
    const guestTicket = accountEvent.tickets.find((ticket) =>
      ticket.planPackageComponentType.includes("GUEST")
    );
    return guestTicket!.planPackageComponentType;
  };

  const findSeatComponentType = () => {
    const seat = accountEvent.tickets.find(
      (ticket) => !ticket.planPackageComponentType.includes("GUEST")
    );
    return seat!.planPackageComponentType;
  };

  const initiateTransfer = async () => {
    if (!account) return;

    const result = await initiateTransferRequest({
      accountId: account.id,
      quantity: selectedIndex.row + 1,
      groupCodeId: accountEvent!.groupCode.id,
      planPackageComponentType:
        type === "guest" ? findGuestComponentType() : findSeatComponentType(),
    });
    setTransferCode(result.transferCode);
  };

  // https://github.com/FanRally/fanrally-core/issues/1226
  // If ticket provider is AXS, do not allow in-app ticket transfer/invite guests
  if (tenantConfig?.ticketProvider === "AXS") {
    const tenantAlias = accountEvent.tickets[0].event.tenantAlias;
    const translationParams = {
      type:
        type === "guest"
          ? i18n.t("components.ShareDialog.guestPasses")
          : i18n.t("components.ShareDialog.seats"),
      appName: tenantConfig.mobileAppName || tenantAlias,
      axsAccountName: account?.extUsername || "N/A",
    };

    return (
      <Dialog
        visible={visible}
        setVisible={setVisible}
        cancelText={i18n.t("common.close")}
      >
        <Text category="h6">{title}</Text>
        <Text style={{ marginVertical: 10 }}>
          {i18n.t("components.ShareDialog.axsShareMessage", translationParams)}
        </Text>
        <Text status="danger">
          {i18n.t("components.ShareDialog.bookingIsFinal")}
        </Text>
      </Dialog>
    );
  }

  return (
    <Dialog
      visible={visible}
      setVisible={setVisible}
      cancelText={i18n.t("common.close")}
      okText={
        transferCode
          ? copied
            ? i18n.t("common.copiedButton")
            : i18n.t("common.shareButton")
          : i18n.t("components.ShareDialog.getCode")
      }
      onOk={() => {
        transferCode ? handleShare(message) : initiateTransfer();
      }}
      okBtnProps={{
        status: "info",
        loading: isLoading,
      }}
      style={{
        width: 375,
      }}
    >
      <Text category="h6">{title}</Text>
      {!transferCode && (
        <>
          <Text style={{ marginVertical: 10 }}>
            {i18n.t("components.ShareDialog.info")}
          </Text>
          <View style={{ marginVertical: 15 }}>
            <Select
              label={i18n.t("components.ShareDialog.max", {
                label,
                quantity: maxQuantity,
              })}
              selectedIndex={selectedIndex}
              onSelect={(index) => setSelectedIndex(index as IndexPath)}
              value={selectedIndex.row + 1}
            >
              {new Array(maxQuantity).fill(0).map((_, index) => (
                <SelectItem key={index} title={index + 1}></SelectItem>
              ))}
            </Select>
          </View>
        </>
      )}

      {!!transferCode && (
        <>
          <Text style={{ marginTop: 10 }}>
            {i18n.t("components.ShareDialog.guestInvitationCode")}
          </Text>
          <Text category="s1" style={{ marginVertical: 5 }}>
            {transferCode}
          </Text>
          <Text>{i18n.t("components.ShareDialog.clickShare")}</Text>
          {shareError && (
            <>
              <Text status="info">
                {i18n.t("common.shareNotSupportedWarning")}
              </Text>
              <Text>{nativeUrl}</Text>
            </>
          )}
        </>
      )}
    </Dialog>
  );
};

export default ShareDialog;
